import { Document } from "@react-pdf/renderer";

import { formatPhone } from "../../../_src_shared/utils/functions/formatPhone";
import PdfPageBrandBar from "../pdfBuilder/pages/PdfPageBrandBar";
import PdfFsFieldTable from "./pages/PdfFsFieldTable";

/**
 * The Funding Shield request sheet ("FS Sheet").
 *
 * ONE continuous table, in the source sheet's own row order and wording — not a
 * dump of the form. The old build fed the whole form object through a generic
 * key/value table, so the sheet printed "Loan Id", "Borrower Type (Individual,
 * Trust, Or Entity)" and "Closing Agent Poc Name": camelCase run through a
 * title-caser. Funding Shield reads this document, so the labels have to be the
 * ones they expect.
 *
 * THE ROW LIST IS CLOSED. These eighteen rows are the sheet; the form captures
 * more than that (the closing agent's company name and postal address, for
 * instance) and those deliberately do NOT print. Adding a row here means the
 * counterparty gets a sheet that doesn't match the one they know, so a new row
 * should come from them, not from a field happening to exist on the form.
 *
 * `includeEmptyValues` (default false) is the one print control: off, rows with
 * no value are dropped; on, every row prints with an em dash so the sheet shows
 * what is still outstanding.
 */

/**
 * Loan amount, with cents — the FS sheet prints "$380,000.00".
 *
 * Deliberately NOT the shared `formatDollar`: that one drops the decimals, and
 * it is also not idempotent — running it over an already-formatted string
 * ("$380,000") yields "$NaN", because `Intl.NumberFormat.format` coerces the
 * non-numeric string to NaN. This guards that case rather than trusting every
 * caller to pass a raw number.
 */
const sheetDollar = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[$,\s]/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const first = (...vals) =>
  vals.find((v) => v !== null && v !== undefined && v !== "") ?? null;

/**
 * Accept the LEGACY field names too.
 *
 * `LoanFeesSubTabFundingShield` (the pre-redesign Fees sub-tab, still routed at
 * `/fees-and-closing/funding-shield`) posts an older key set — `loanId`,
 * `guarantorNames`, `correspondent`, the literal `"Borrower Type (Individual,
 * Trust, or Entity)"`. Normalizing here means one document definition serves
 * both surfaces instead of the legacy one silently printing a page of blanks.
 */
const normalize = (d = {}) => ({
  ...d,
  loanNumber: first(d.loanNumber, d.loanId),
  borrowerType: first(
    d.borrowerType,
    d["Borrower Type (Individual, Trust, or Entity)"],
  ),
  borrowerName: first(d.borrowerName, d.guarantorNames),
  correspondentName: first(d.correspondentName, d.correspondent),
  closingAgentPOCEmail: first(
    d.closingAgentPOCEmail,
    d.closingAgentPOCEmailAddress,
  ),
});

/** The sheet, in order. */
const buildRows = (d) => [
  { label: "Loan No.", value: d.loanNumber },
  // No hardcoded fallback address. The lender block comes from the loan's
  // funding entity (`loanTerms.fundingEntity.mortgageeClause`); printing a
  // stand-in address on a wire-fraud verification sheet when the real one is
  // missing is worse than printing a blank the closer has to fill.
  { label: "Lender Name and Address", value: d.lenderNameAndAddress },
  {
    label: "Borrower Type (Individual, Trust, or Entity)",
    // Mirrors the builder's own default: these are entity-borrower deals, so a
    // sheet that arrives without a type prints "Entity".
    value: d.borrowerType || "Entity",
  },
  { label: "Borrower Name", value: d.borrowerName },
  { label: "Borrower Trust/Entity Name", value: d.borrowerEntityName },
  { label: "Subject Property Address", value: d.subjectPropertyAddress },
  { label: "City", value: d.subjectPropertyCity },
  { label: "State", value: d.subjectPropertyState },
  { label: "Zip", value: d.subjectPropertyZip },
  { label: "Total Loan Amount", value: sheetDollar(d.totalLoanAmount) },
  { label: "Account Manager Name", value: d.accountManagerName },
  { label: "Account Manager Email", value: d.accountManagerEmail },
  {
    label: "Account Manager Phone",
    value: d.accountManagerPhone ? formatPhone(d.accountManagerPhone) : null,
  },
  { label: "Correspondent", value: d.correspondentName },
  { label: "Correspondent POC Name", value: d.correspondentPOCName },
  { label: "Correspondent POC Email", value: d.correspondentPOCEmail },
  { label: "Closing Agent POC Name", value: d.closingAgentPOCName },
  { label: "Closing Agent POC Email Address", value: d.closingAgentPOCEmail },
];

const FundingShieldPDF = ({ formData }) => {
  const d = normalize(formData || {});

  return (
    <Document>
      <PdfPageBrandBar
        pageNumber={"1 of 1"}
        pageTitle="FS Sheet"
        pageSubtitle={d.loanNumber ? `Loan ${d.loanNumber}` : "Funding Shield"}
        date={new Date().toISOString().split("T")[0]}
      >
        <PdfFsFieldTable
          rows={buildRows(d)}
          hideEmpty={d.includeEmptyValues !== true}
        />
      </PdfPageBrandBar>
    </Document>
  );
};

export default FundingShieldPDF;
